import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import qs from 'qs';
import moment from 'moment';
import csv from 'csv-stringify';

import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import intl from '../../shared/intl';

import { create as paginationCreate } from '../../shared/pagination';
import createCalendar from '../../shared/calendar';
import createFilters from '../../shared/filters';
import createSaveExport from '../components/save-export';
import filtersInitialState from '../shared/filter-config';

import { tableInitialState, transformRows } from '../components/table/conversions';
import {
	getRequestQuery,
	mapQueryFilters,
	getCurrentCurrency,
	toggleInitialCurrency,
	initCurrencies,
	initDefaultCurrenciesAction
} from '../shared/utils';

import { REFETCH_ALL_DATA } from '../../root';

import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import find from  'lodash/find';

import { APPLY_MASTER_SWITCH } from '../general';

const isRocket = process.env.CPA_ENV === 'rocket';

const pagination = paginationCreate('conversions', {
	ConversionsTable: {
		currentPage: 1,
		perPage: 1000
	}
});

const calendar = createCalendar({
	namespace: 'conversions',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filterInitialState = [].concat(filtersInitialState, {
	title: 'Статус',
	suggest: [{
		placeholder: 'Выберите статус',
		key: 'statuses',
		options: {
			source: 'app.lists.conversion-statuses',
			type: 'fromState'
		}
	}]
});

const filters = createFilters({
	namespace: 'conversions',
	stateNodeName: 'filters',
	filtersInitialState: filterInitialState
});

const saveExport = createSaveExport({
	namespace: 'conversions'
});

const CONVERSIONS_FETCH_DATA_SUCCESS = 'conversions/fetch-data-success';
const CONVERSIONS_FETCH_DATA_ERROR = 'conversions/fetch-data-error';

const CONVERSIONS_TOGGLE_DETAILS = 'conversions/toggle-details';
const SORT_BY_COLUMN = 'conversions/table-sort-by-column';
const CONVERSIONS_APPLY_CALENDAR_VALUE = 'conversions/apply-calendar-value';
const CONVERSIONS_UPDATE_QUERY = 'conversions/update-query';

const CONVERSIONS_RESET = 'conversions/reset';
const SHOW_PROGRESS_BAR = 'conversions/show-progress-bar';

const CHANGE_CURRENCY = 'conversions/change-currency';

const CONVERSIONS_TOGGLE_PLAYER = 'conversions/toggle-player';

const GET_LAST_CONVERSION_CURRENCY_SUCCESS = 'conversions/get-last-conversion-success';
const GET_LAST_CONVERSION_CURRENCY_ERROR = 'conversions/get-last-conversion-error';
const INIT_CURRENCIES = 'conversions/init-currencies';

const initialState = {
	data: null,
	query: {
		currency: null,
		groups: ['date']
	},
	table: tableInitialState,
	pagination: pagination.initState,
	calendar: calendar.initialState,
	filters: filters.initialState,
	fullAppliedFilters: null,
	listOfCurrencies: [],
	savedFilters: saveExport.initialState,
	progressBar: false,
	error: false
};

export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			initDefaultCurrenciesAction(getState, dispatch, INIT_CURRENCIES, 'conversions');
			const masterSwitchActiveTab = selectors.getMasterSwitchActiveTab(getState());
			const request = http({
				url: `${endpoints.STATISTICS.GET_LAST_CONVERSION}?owner_type=${masterSwitchActiveTab}`,
				method: 'get',
				actions: [
					GET_LAST_CONVERSION_CURRENCY_SUCCESS,
					GET_LAST_CONVERSION_CURRENCY_ERROR
				]
			});
			request(dispatch).then(() => fetchRequest(dispatch, getState, props));
		},

		statsApply: () => (dispatch, getState) => {
			dispatch({
				type: SHOW_PROGRESS_BAR
			});

			dispatch(actions.updateQuery());

			fetchRequest(dispatch, getState);
		},

		statsReset: () => dispatch => {
			dispatch(replace('statistics/conversions/'));
			dispatch({ type: CONVERSIONS_RESET });
		},
		statsResetFromSavedFilter: () => ({ type: CONVERSIONS_RESET }),

		sortByColumn: columnIndex => ({
			type: SORT_BY_COLUMN,
			payload: columnIndex
		}),

		toggleDetails: rowIndex => ({
			type: CONVERSIONS_TOGGLE_DETAILS,
			payload: rowIndex
		}),

		togglePlayer: () => (dispatch, getState) => {
			const isPlayed = getState().conversions.table.isPlayed;
			dispatch({
				type: CONVERSIONS_TOGGLE_PLAYER,
				payload: !isPlayed
			});
		},

		applyCalendarValue: calendarValue => ({
			type: CONVERSIONS_APPLY_CALENDAR_VALUE,
			payload: calendarValue
		}),

		pagination: () => dispatch => pagination.actions(dispatch),

		conversionsFilterApply: filterId => (dispatch, getState) => {
			filters.actions.filterApply(filterId)(dispatch, getState);
		},

		updateQuery: (externalQuery) => ({
			type: CONVERSIONS_UPDATE_QUERY,
			payload: externalQuery
		}),

		resetUnappliedFilters: () => (dispatch, getState) => {
			let currentQuery = getState().conversions.query;
			let fullAppliedFilters = getState().conversions.fullAppliedFilters;

			dispatch(
				filters.actions.filterApplyByExternalQuery({
					...currentQuery,
					filters: fullAppliedFilters
				})
			);
			currentQuery && currentQuery.date_range && dispatch(actions.applyCalendarValue(currentQuery.date_range));
		},

		showProgressBar: () => ({
			type: SHOW_PROGRESS_BAR
		}),

		changeCurrency: currencyId => dispatch => {
			dispatch({
				type: CHANGE_CURRENCY,
				payload: currencyId
			});

			dispatch(actions.statsApply());
		},

		statExport: () => (dispatch, getState) => {
			const { paginatedData: { data } } = selectors.getTableData(getState());
			const cancellationReasons = getState().app.lists['conversion-cancellation-reasons'];

			if (data.length === 0) return;

			const headers = [
				'id', 'date', 'sid1', 'sid2', 'sid3', 'sid4', 'sid5',
				'offer', 'country', 'country_code', 'city', 'ip',
				'status', 'cancellation_reason', 'payout', 'currency',
				'campaign', 'campaign_id', 'campaign_type', 'click_referer',
				'contacts', 'first_called_at', 'calls_count', 'status_changed_at',
				'referer', 'landing', 'transit', 'client_age', 'client_gender'
			];
			const columns = data.map(entity => {
				return headers.map(index => {
					if (index === 'cancellation_reason') {
						const value = entity[index] ? entity[index] : '';
						const cancellationReason = find(cancellationReasons, { id: value });
						return cancellationReason ? cancellationReason['text'] : value;
					}
					return entity[index] || '-';
				});
			});

			const rows = [].concat([headers], columns);

			csv(rows, (err, res) => {
				const exportFilename = `Export--Convertions--${moment().format(
					'YYYY-MM-DD--HH-mm-ss'
				)}.csv`;
				const csvData = new Blob([res], { type: 'text/csv;charset=utf-8;' });
				//IE11 & Edge
				if (navigator.msSaveBlob) {
					navigator.msSaveBlob(csvData, exportFilename);
				} else {
					//In FF link must be added to DOM to be clicked
					const link = document.createElement('a');
					link.href = window.URL.createObjectURL(csvData);
					link.setAttribute('download', exportFilename);
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				}
			});
		},

		applyMasterSwitch: id => dispatch => {
			http({
				url: `${endpoints.STATISTICS.GET_LAST_CONVERSION}?owner_type=${id}`,
				method: 'get',
				actions: [
					GET_LAST_CONVERSION_CURRENCY_SUCCESS,
					GET_LAST_CONVERSION_CURRENCY_ERROR
				]
			})(dispatch)
				.then(() => {
					return dispatch({ type: APPLY_MASTER_SWITCH, payload: id });
				});
		}
	},
	filters.actions,
	calendar.actions,
	saveExport.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			const shouldSaveFiltersState =
				action.payload.pathname.indexOf('statistics/analytics') !== -1 ||
				action.payload.pathname.indexOf('statistics/general') !== -1 ||
				action.payload.pathname.indexOf('statistics/conversions') !== -1;
			return shouldSaveFiltersState ? { ...state, data: null } : initialState;
		},
		[CONVERSIONS_FETCH_DATA_SUCCESS]: (state, action) => {
			const [data, savedFilters] = action.payload.map(res => res.data);
			return { ...state,
				data,
				savedFilters: { ...state.savedFilters, data: savedFilters },
				showProgressBar: false
			};
		},
		[CONVERSIONS_FETCH_DATA_ERROR]: (state, action) => ({ ...state, data: null, error: true, showProgressBar: false }),
		[GET_LAST_CONVERSION_CURRENCY_ERROR]:
				(state, action) => ({ ...state, data: null, error: true, showProgressBar: false }),

		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns } = state.table;
			const columnKey = action.payload;

			const columnDirection = sortByColumn && sortByColumn['key'] === columnKey
				? sortByColumn.direction === 'desc' ? 'asc' : 'desc'
				: columns[action.payload].direction;

			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnKey],
						key: action.payload,
						direction: columnDirection
					}
				}
			};
		},

		[CONVERSIONS_TOGGLE_DETAILS]: (state, action) => {
			return {
				...state,
				table: {
					...state.table,
					detailsIndex: state.table.detailsIndex === action.payload ? null : action.payload
				}
			};
		},

		[CONVERSIONS_TOGGLE_PLAYER]: (state, action) => {
			return {
				...state,
				table: {
					...state.table,
					isPlayed: action.payload
				}
			};
		},

		[CONVERSIONS_APPLY_CALENDAR_VALUE]: (state, action) => {
			const dateRange = action.payload;
			const calendar = {
				...state.calendar,
				value: {
					start: (dateRange.start && moment(dateRange.start).format('YYYY-MM-DD')) || state.calendar.value.start,
					end: (dateRange.end && moment(dateRange.end).format('YYYY-MM-DD')) || state.calendar.value.end
				}
			};

			return { ...state, calendar };
		},

		[CONVERSIONS_UPDATE_QUERY]: (state, action) => {
			const filters = state.filters && mapQueryFilters(state.filters);
			const fullFilters = state.filters && mapQueryFilters(state.filters, false);
			const date_range = state.calendar && state.calendar.value;

			return {
				...state,
				query: {
					...state.query,
					currency: action.payload && action.payload.currency || state.query.currency,
					filters: filters || state.query.filters,
					date_range: date_range || state.query.date_range
				},
				fullAppliedFilters: fullFilters
			};
		},

		[CONVERSIONS_RESET]: () => initialState,
		[REFETCH_ALL_DATA]: () => initialState,

		[SHOW_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),

		[CHANGE_CURRENCY]: (state, action) => ({
			...state,
			query: {
				...state.query,
				currency: action.payload
			}
		}),

		[APPLY_MASTER_SWITCH]: (state, action) => ({
			...state,
			data: null
		}),
		[GET_LAST_CONVERSION_CURRENCY_SUCCESS]: (state, action) => isRocket ? state : toggleInitialCurrency(state, action),
		[INIT_CURRENCIES]: (state, action) => initCurrencies(state, action)
	},
	pagination.reducers,
	filters.reducers,
	calendar.reducers,
	saveExport.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

export const selectors = {
	getData: state => state.conversions.data,
	getTableData: state => ({
		tableHeaders: values(state.conversions.table.columns),
		paginatedData: paginatedData(state),
		detailsIndex: state.conversions.table.detailsIndex,
		columnSorting: state.conversions.table.sortByColumn,
		isPlayed: state.conversions.table.isPlayed
	}),
	getFilters: state => filters.selectors.selectFilters(state => state.conversions.filters, state => state)(state),
	getAppliedFilters: state => filters.selectors.appliedFilters(state),
	getCalendar: state => calendar.selectors.getCalendar(state => state.conversions.calendar, state => state)(state),
	getSavedFilters: state =>
		saveExport.selectors.getSavedFilters(state => state.conversions.savedFilters, state => state)(state),
	getCancellationReasonsList: state => state.app.lists && state.app.lists[`conversion-cancellation-reasons`],
	getConversionStatusesList: state => state.app.lists && state.app.lists[`conversion-statuses`],
	getProgressBar: state => state.conversions.showProgressBar,
	getListOfCurrencies: state => state.app.userData.balances.map(item => ({
		id: item.currency,
		text: item.currency
	})),
	getActiveCurrency: state => getCurrentCurrency(state, 'conversions'),
	getGendersList: state => state.app.lists.genders,
	getMasterSwitchConfig: state => state.statistics.masterSwitchConfig,
	getMasterSwitchActiveTab: state => state.statistics.masterSwitchConfig.filter(i => i.active)[0].id,
	getIsMasterStatus: state => state.app.userData.general.is_master,
	getError: state => state.conversions.error,
	getIntl: intl
};

const paginatedData = state =>
	pagination.selectors.getPageData(
		state => state.conversions.data && transformRows(state.conversions.data, state.conversions.table.sortByColumn),
		state => state.conversions.pagination.ConversionsTable.perPage,
		state => state.conversions.pagination.ConversionsTable.currentPage
	)(state);

const fetchRequest = (dispatch, getState, props) => {
	const state = getState();
	const user = state.app.userData.general;
	const activeBalance = getCurrentCurrency(state, 'conversions');
	const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};
	const query = qs.stringify(
		getRequestQuery(
			state.conversions,
			activeBalance, externalQuery,
			false,
			user.is_master,
			state.statistics.masterSwitchConfig
		)
	);
	const activeMasterSwitchSetting = find(state.statistics.masterSwitchConfig, { active: true }).id;

	const request = http({
		url: [`${endpoints.STATISTICS.CONVERSIONS}?${query}`, `${endpoints.PROFILE.SAVED_FILTERS}`],
		method: 'get',
		actions: [CONVERSIONS_FETCH_DATA_SUCCESS, CONVERSIONS_FETCH_DATA_ERROR]
	});

	return request(dispatch).then(action => {
		if (action.type === CONVERSIONS_FETCH_DATA_SUCCESS) {
			if (user.is_master && activeMasterSwitchSetting !== 'master_only') {
				dispatch(filters.actions.filterReplaceState(
					[].concat(filterInitialState, {
						title: 'Вебмастера',
						suggest: [{
							placeholder: 'Выберите вебмастера',
							key: 'affiliates',
							options: {
								source: endpoints.STATISTICS.FILTERS.AFFILIATES,
								type: 'fromServer'
							}
						}]
					})
				));
			}
			if (!isEmpty(externalQuery)) {
				dispatch(filters.actions.filterApplyByExternalQuery(externalQuery));

				if (!isEmpty(externalQuery.date_range)) {
					dispatch(actions.applyCalendarValue(externalQuery.date_range));
				}

				dispatch(actions.updateQuery(externalQuery));
			} else {
				dispatch(actions.resetUnappliedFilters());
			}
		}
		return action;
	});
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/statistics/conversions/index.js